<script setup lang="ts">
import { computed } from "vue";
import { UiButton, UiFieldContainer, UiIcon, UiText } from "@shopexperts/shared";
import type { CustomerStoryMetric } from "@shopexperts/shared";

const props = defineProps<{
    title: string;
    subtitle: string;
    mode: "before" | "after";
}>();

const metrics = defineModel<CustomerStoryMetric[]>({ default: [] });
const emit = defineEmits<{
    (e: "edit-metrics"): void;
}>();

const valueKey = computed(() => props.mode === "before" ? "before_value" : "after_value");
</script>

<template>
    <div class="flex flex-col gap-5">
        <div class="flex items-center justify-between gap-4">
            <UiText as="p" variant="h6" class="font-semibold text-se-dark">
                {{ props.title }}
                <span class="font-normal text-se-gray-700">({{ props.subtitle }})</span>
            </UiText>
            <UiButton variant="outline" size="sm" label="Add Metrics" @click="emit('edit-metrics')">
                <template #before_label>
                    <UiIcon name="plus-square" class="h-4 w-4" />
                </template>
            </UiButton>
        </div>

        <div v-if="metrics.length" class="flex flex-wrap gap-x-10 gap-y-5">
            <div
                v-for="(metric, index) in metrics"
                :key="`${metric.label}-${index}`"
                class="flex min-w-0 flex-[1_1_220px] flex-col gap-1 md:basis-[calc((100%-7.5rem)/4)]"
            >
                <UiText as="label" variant="small" class="text-se-dark" :text="metric.label" />
                <UiFieldContainer flexable class="min-h-16 bg-white px-4 py-4">
                    <div class="flex w-full items-center justify-center gap-1">
                        <UiText v-if="metric.prefix" as="span" variant="h4" class="font-medium text-se-dark" :text="metric.prefix" />
                        <input
                            v-model="metric[valueKey]"
                            type="text"
                            class="w-full min-w-0 bg-transparent text-center font-archivo se-font-h4 font-medium text-se-dark outline-none"
                        />
                        <UiText v-if="metric.suffix" as="span" variant="h4" class="font-medium text-se-dark" :text="metric.suffix" />
                    </div>
                </UiFieldContainer>
            </div>
        </div>

        <div v-else class="rounded-lg border border-dashed border-se-gray-300 bg-white px-4 py-5">
            <UiText
                as="p"
                variant="small"
                class="text-se-gray-700"
                text="Adding baseline and outcome metrics makes your case study more credible and easier to scan. Concrete numbers help clients quickly understand the business impact of your work, compare before-and-after performance, and feel more confident that your results are real and repeatable."
            />
        </div>
    </div>
</template>
